import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFieldAuth } from '../contexts/FieldContext';
import * as checkInService from '../services/checkInService';
import * as waiverService from '../services/waiverService';
import { CheckIn, TodayCheckInsResponse, PlayerSearchResult, SignedWaiver, WaiverTemplate } from '../types/waiver';
import { FeatureGate } from '../components/subscription';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirmModal } from '../hooks/useConfirmModal';

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: 140px;
`;

const Header = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const HeaderTabs = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  padding: 4px;
  border-radius: 8px;
`;

const HeaderTab = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#000' : theme.colors.text};

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.surface};
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TodayStats = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
`;

const BackButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const MainContent = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const SearchSection = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 14px 18px;
  font-size: 16px;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 400;
  }
`;

const RFIDInput = styled(SearchInput)`
  max-width: 300px;
  font-family: monospace;
`;

const QuickActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'warning' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'success':
        return `
          background: ${theme.colors.success};
          color: white;
          &:hover { opacity: 0.9; }
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning};
          color: #000;
          &:hover { opacity: 0.9; }
        `;
      case 'danger':
        return `
          background: transparent;
          color: ${theme.colors.error};
          border: 1px solid ${theme.colors.error};
          &:hover { opacity: 0.8; }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: #000;
          &:hover { opacity: 0.9; }
        `;
    }
  }}
`;

const ResultsSection = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  overflow: hidden;
`;

const ResultsHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultsTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  font-weight: 600;
`;

const ResultsList = styled.div`
  max-height: 500px;
  overflow-y: auto;
`;

const PlayerCard = styled.div<{ $status?: 'valid' | 'expired' | 'none' }>`
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PlayerAvatar = styled.div<{ $status?: 'valid' | 'expired' | 'none' }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: white;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'valid': return theme.colors.success;
      case 'expired': return theme.colors.warning;
      default: return theme.colors.error;
    }
  }};
`;

const PlayerDetails = styled.div``;

const PlayerName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const GuestBadge = styled.span`
  color: ${({ theme }) => theme.colors.warning};
  font-weight: 600;
`;

const PlayerMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  margin-top: 2px;
`;

const PlayerActions = styled.div`
  display: flex;
  gap: 8px;
`;

const CheckInButton = styled.button<{ $checked?: boolean }>`
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $checked, theme }) => $checked ? `
    background: ${theme.colors.success};
    color: white;
  ` : `
    background: ${theme.colors.primary};
    color: #000;
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WaiverBadge = styled.span<{ $status: 'valid' | 'expired' | 'none' }>`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;

  ${({ $status, theme }) => {
    switch ($status) {
      case 'valid':
        return `background: rgba(76, 175, 80, 0.2); color: ${theme.colors.success};`;
      case 'expired':
        return `background: rgba(255, 152, 0, 0.2); color: ${theme.colors.warning};`;
      default:
        return `background: rgba(244, 67, 54, 0.2); color: ${theme.colors.error};`;
    }
  }}
`;

const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RecentCheckIns = styled.div`
  margin-top: 24px;
`;

const CheckInItem = styled.div`
  padding: 12px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const CheckInTime = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const GameTypeModal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 32px;
  width: 450px;
  max-width: 90vw;
`;

const ModalTitle = styled.h2`
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 20px;
  font-weight: 600;
`;

const ModalSubtitle = styled.p`
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const PlayerInfoBox = styled.div`
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const PlayerInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PlayerInfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

const PlayerInfoValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 500;
`;

const GameTypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
`;

const GameTypeButton = styled.button<{ $selected: boolean }>`
  padding: 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.border)};
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'transparent')};
  color: ${({ $selected, theme }) => ($selected ? '#000' : theme.colors.text)};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'cancel' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${({ $variant, theme }) => $variant === 'primary' ? `
    background: ${theme.colors.success};
    color: white;
    border: none;
  ` : `
    background: transparent;
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.border};
  `}

  &:hover {
    opacity: 0.9;
  }
`;

const RfidButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const RfidInput = styled.input`
  width: 100%;
  padding: 14px 18px;
  font-size: 16px;
  font-family: monospace;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 24px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const GAME_TYPES = ['Airsoft', 'Paintball', 'Laser Tag', 'Private Event', 'Training', 'Other'];

const FieldManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { field, isFieldAuthenticated } = useFieldAuth();
  const rfidInputRef = useRef<HTMLInputElement>(null);

  const tabFromUrl = searchParams.get('tab') as 'checkin' | 'signed' | 'templates' | null;
  const [activeTab, setActiveTab] = useState<'checkin' | 'signed' | 'templates'>(tabFromUrl || 'checkin');
  const { confirm, modalProps } = useConfirmModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [rfidInput, setRfidInput] = useState('');
  const [recentCheckIns, setRecentCheckIns] = useState<CheckIn[]>([]);
  const [searchResults, setSearchResults] = useState<PlayerSearchResult[]>([]);
  const [todayStats, setTodayStats] = useState({ total: 0, confirmed: 0 });
  const [loading, setLoading] = useState(false);

  // Game type modal state
  const [showGameTypeModal, setShowGameTypeModal] = useState(false);
  const [selectedCheckIn, setSelectedCheckIn] = useState<CheckIn | null>(null);
  const [selectedGameType, setSelectedGameType] = useState(GAME_TYPES[0]);

  // RFID modal state
  const [showRfidModal, setShowRfidModal] = useState(false);
  const [rfidCheckIn, setRfidCheckIn] = useState<CheckIn | null>(null);
  const [newRfidTag, setNewRfidTag] = useState('');

  // Templates state
  const [templates, setTemplates] = useState<WaiverTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [copyModal, setCopyModal] = useState<{ show: boolean; templateId: string | null }>({
    show: false,
    templateId: null,
  });
  const [copyYear, setCopyYear] = useState(new Date().getFullYear() + 1);

  // Signed waivers state
  const [signedWaivers, setSignedWaivers] = useState<SignedWaiver[]>([]);
  const [signedWaiversLoading, setSignedWaiversLoading] = useState(false);
  const [signedWaiversYear, setSignedWaiversYear] = useState<number>(new Date().getFullYear());
  const [signedWaiversSearch, setSignedWaiversSearch] = useState('');
  const [signedWaiversSearchInput, setSignedWaiversSearchInput] = useState('');
  const [signedWaiversPage, setSignedWaiversPage] = useState(1);
  const [signedWaiversTotalPages, setSignedWaiversTotalPages] = useState(1);
  const [signedWaiversTotalCount, setSignedWaiversTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!isFieldAuthenticated) {
      navigate('/field/login');
      return;
    }
    loadTodayData();
  }, [isFieldAuthenticated, navigate]);

  // Keep focus on RFID input for scanner
  useEffect(() => {
    if (activeTab !== 'checkin') return;

    const keepFocus = () => {
      if (rfidInputRef.current && document.activeElement !== rfidInputRef.current) {
        // Don't steal focus from search input
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        if (document.activeElement !== searchInput) {
          rfidInputRef.current.focus();
        }
      }
    };

    // Initial focus
    keepFocus();

    // Re-focus after clicks (with delay to allow button clicks to complete)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't refocus if clicking buttons or inputs
      if (target.tagName === 'BUTTON' || target.tagName === 'INPUT') return;
      setTimeout(keepFocus, 200);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [activeTab]);

  const loadSignedWaivers = async (year: number, search: string, page: number) => {
    setSignedWaiversLoading(true);
    try {
      const data = await waiverService.getSignedWaivers(year, search || undefined, page, PAGE_SIZE);
      setSignedWaivers(data.items);
      setSignedWaiversTotalPages(data.totalPages);
      setSignedWaiversTotalCount(data.totalCount);
    } catch (err) {
      console.error('Failed to load signed waivers:', err);
    } finally {
      setSignedWaiversLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'signed') {
      loadSignedWaivers(signedWaiversYear, signedWaiversSearch, signedWaiversPage);
    }
  }, [activeTab, signedWaiversYear, signedWaiversPage]);

  const handleSignedWaiversSearch = () => {
    setSignedWaiversSearch(signedWaiversSearchInput);
    setSignedWaiversPage(1);
    loadSignedWaivers(signedWaiversYear, signedWaiversSearchInput, 1);
  };

  const handleClearSignedWaiversSearch = () => {
    setSignedWaiversSearchInput('');
    setSignedWaiversSearch('');
    setSignedWaiversPage(1);
    loadSignedWaivers(signedWaiversYear, '', 1);
  };

  // Templates functions
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const data = await waiverService.getAllTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'templates' && templates.length === 0) {
      loadTemplates();
    }
  }, [activeTab]);

  const handleDeleteTemplate = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Template',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await waiverService.deleteTemplate(id);
      setTemplates(templates.filter((t) => t.id !== id));
    } catch (err: any) {
      console.error('Failed to delete template:', err);
    }
  };

  const handleActivateTemplate = async (id: string) => {
    try {
      await waiverService.activateTemplate(id);
      loadTemplates();
    } catch (err: any) {
      console.error('Failed to activate template:', err);
    }
  };

  const handleCopyTemplate = async () => {
    if (!copyModal.templateId) return;

    try {
      await waiverService.copyTemplate(copyModal.templateId, copyYear);
      setCopyModal({ show: false, templateId: null });
      loadTemplates();
    } catch (err: any) {
      console.error('Failed to copy template:', err);
    }
  };

  const loadTodayData = async () => {
    try {
      const response: TodayCheckInsResponse = await checkInService.getTodayCheckIns();
      setRecentCheckIns(response.checkIns.slice(0, 10));
      setTodayStats({
        total: response.totalCount,
        confirmed: response.confirmedCount,
      });
    } catch (err) {
      console.error('Failed to load today data:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const results = await checkInService.searchPlayers(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRFIDScan = async (rfid: string) => {
    if (!rfid.trim()) return;

    setLoading(true);
    try {
      // Use RFID scan endpoint
      await checkInService.processRfidScan({ rfidTag: rfid });
      // Clear search and refresh check-ins list
      setSearchResults([]);
      setSearchQuery('');
      await loadTodayData();
    } catch (err) {
      console.error('RFID scan failed:', err);
    } finally {
      setLoading(false);
      setRfidInput('');
      rfidInputRef.current?.focus();
    }
  };

  const handleCheckIn = async (player: PlayerSearchResult) => {
    try {
      if (player.userId) {
        await checkInService.createCheckInForUser({ userId: player.userId });
      } else if (player.signedWaiverId) {
        await checkInService.createCheckInForGuest({ signedWaiverId: player.signedWaiverId });
      }
      // Refresh and clear search
      setSearchResults([]);
      setSearchQuery('');
      await loadTodayData();
    } catch (err) {
      console.error('Check-in failed:', err);
    }
  };

  const openConfirmModal = (checkIn: CheckIn) => {
    setSelectedCheckIn(checkIn);
    setSelectedGameType(GAME_TYPES[0]);
    setShowGameTypeModal(true);
  };

  const closeConfirmModal = () => {
    setShowGameTypeModal(false);
    setSelectedCheckIn(null);
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedCheckIn) return;

    try {
      await checkInService.confirmCheckIn(selectedCheckIn.id, { gameType: selectedGameType });
      closeConfirmModal();
      await loadTodayData();
    } catch (err) {
      console.error('Confirm check-in failed:', err);
    }
  };

  const openRfidModal = (checkIn: CheckIn) => {
    setRfidCheckIn(checkIn);
    setNewRfidTag(checkIn.rfidTag || '');
    setShowRfidModal(true);
  };

  const closeRfidModal = () => {
    setShowRfidModal(false);
    setRfidCheckIn(null);
    setNewRfidTag('');
  };

  const handleUpdateRfid = async () => {
    if (!rfidCheckIn || !newRfidTag.trim()) return;

    try {
      await checkInService.updateRfidTag(rfidCheckIn.id, rfidCheckIn.userId, newRfidTag.trim());
      closeRfidModal();
      await loadTodayData();
    } catch (err) {
      console.error('Failed to update RFID:', err);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  return (
    <FeatureGate feature="management" featureName="Management Dashboard">
    <PageContainer>
      <Header>
        <HeaderLeft>
          <Logo>NW Management</Logo>
          <HeaderTabs>
            <HeaderTab $active={activeTab === 'checkin'} onClick={() => setActiveTab('checkin')}>
              Check-In
            </HeaderTab>
            <HeaderTab $active={activeTab === 'signed'} onClick={() => setActiveTab('signed')}>
              Signed Waivers
            </HeaderTab>
            <HeaderTab $active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>
              Templates
            </HeaderTab>
          </HeaderTabs>
        </HeaderLeft>
        <HeaderRight>
          <TodayStats>
            <StatItem>
              <StatValue>{todayStats.total}</StatValue>
              <StatLabel>Total</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{todayStats.confirmed}</StatValue>
              <StatLabel>Confirmed</StatLabel>
            </StatItem>
          </TodayStats>
          <BackButton onClick={() => navigate(`/fields/${field?.id}`)}>
            Back to Field
          </BackButton>
        </HeaderRight>
      </Header>

      <MainContent>
        {activeTab === 'checkin' && (
          <>
            <SearchSection>
              <SearchRow>
                <RFIDInput
                  ref={rfidInputRef}
                  type="text"
                  placeholder="Scan RFID card..."
                  value={rfidInput}
                  onChange={(e) => setRfidInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRFIDScan(rfidInput);
                    }
                  }}
                />
                <SearchInput
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <ActionButton onClick={handleSearch} disabled={loading}>
                  {loading ? 'Searching...' : 'Search'}
                </ActionButton>
              </SearchRow>
            </SearchSection>

            <ResultsSection>
              <ResultsHeader>
                <ResultsTitle>
                  {searchResults.length > 0
                    ? `Search Results (${searchResults.length})`
                    : `Today's Check-Ins (${recentCheckIns.length})`}
                </ResultsTitle>
                {searchResults.length > 0 && (
                  <ActionButton onClick={() => { setSearchResults([]); setSearchQuery(''); }}>
                    Clear Search
                  </ActionButton>
                )}
              </ResultsHeader>

              {searchResults.length > 0 ? (
                <ResultsList>
                  {searchResults.map((player) => {
                    const displayName = player.fullName || player.callsign || 'Unknown';
                    return (
                      <PlayerCard key={player.userId || player.signedWaiverId} $status={player.hasValidWaiver ? 'valid' : 'none'}>
                        <PlayerInfo>
                          <PlayerAvatar $status={player.hasValidWaiver ? 'valid' : 'none'}>
                            {getInitials(displayName)}
                          </PlayerAvatar>
                          <PlayerDetails>
                            <PlayerName>
                              {displayName}
                              {player.callsign && player.fullName && ` (${player.callsign})`}
                            </PlayerName>
                            <PlayerMeta>
                              {player.dateOfBirth && `DOB: ${new Date(player.dateOfBirth).toLocaleDateString()} • `}
                              {player.email || 'No email'}
                              {player.phone && ` • ${player.phone}`}
                            </PlayerMeta>
                          </PlayerDetails>
                        </PlayerInfo>
                        <PlayerActions>
                          <WaiverBadge $status={player.hasValidWaiver ? 'valid' : 'none'}>
                            {player.hasValidWaiver ? 'Waiver OK' : 'No Waiver'}
                          </WaiverBadge>
                          <CheckInButton
                            onClick={() => handleCheckIn(player)}
                            disabled={!player.hasValidWaiver}
                          >
                            Check In
                          </CheckInButton>
                        </PlayerActions>
                      </PlayerCard>
                    );
                  })}
                </ResultsList>
              ) : recentCheckIns.length > 0 ? (
                <ResultsList>
                  {recentCheckIns.map((checkIn) => {
                    const displayName = checkIn.playerInfo.fullName || checkIn.playerInfo.name;
                    return (
                    <PlayerCard key={checkIn.id} $status={checkIn.waiverStatus?.hasValidWaiver ? 'valid' : 'none'}>
                      <PlayerInfo>
                        <PlayerAvatar $status={checkIn.waiverStatus?.hasValidWaiver ? 'valid' : 'none'}>
                          {getInitials(displayName)}
                        </PlayerAvatar>
                        <PlayerDetails>
                          <PlayerName>
                            {displayName}
                            {checkIn.playerInfo.fullName && checkIn.playerInfo.name !== checkIn.playerInfo.fullName &&
                              ` (${checkIn.playerInfo.name})`}
                          </PlayerName>
                          <PlayerMeta>
                            {checkIn.playerInfo.dateOfBirth && `DOB: ${new Date(checkIn.playerInfo.dateOfBirth).toLocaleDateString()} • `}
                            {checkIn.playerInfo.email || 'No email'}
                            {checkIn.playerInfo.phone && ` • ${checkIn.playerInfo.phone}`}
                          </PlayerMeta>
                        </PlayerDetails>
                      </PlayerInfo>
                      <PlayerActions>
                        <WaiverBadge $status={checkIn.waiverStatus?.hasValidWaiver ? 'valid' : 'none'}>
                          {checkIn.waiverStatus?.hasValidWaiver ? 'Waiver OK' : 'No Waiver'}
                        </WaiverBadge>
                        <RfidButton onClick={() => openRfidModal(checkIn)}>
                          {checkIn.rfidTag ? 'RFID' : 'Add RFID'}
                        </RfidButton>
                        {checkIn.isConfirmed ? (
                          <CheckInButton $checked>
                            ✓ {checkIn.gameType}
                          </CheckInButton>
                        ) : (
                          <CheckInButton
                            onClick={() => openConfirmModal(checkIn)}
                            disabled={!checkIn.waiverStatus?.hasValidWaiver}
                          >
                            Confirm
                          </CheckInButton>
                        )}
                      </PlayerActions>
                    </PlayerCard>
                    );
                  })}
                </ResultsList>
              ) : (
                <EmptyState>
                  No check-ins yet today. Scan an RFID card or search for a player to begin.
                </EmptyState>
              )}
            </ResultsSection>
          </>
        )}

        {activeTab === 'signed' && (
          <>
            <SearchSection>
              <SearchRow>
                <SearchInput
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={signedWaiversSearchInput}
                  onChange={(e) => setSignedWaiversSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSignedWaiversSearch();
                    }
                  }}
                />
                <select
                  value={signedWaiversYear}
                  onChange={(e) => {
                    setSignedWaiversYear(Number(e.target.value));
                    setSignedWaiversPage(1);
                  }}
                  style={{
                    padding: '14px 18px',
                    borderRadius: '8px',
                    border: '2px solid #444',
                    background: '#1a1a1a',
                    color: '#fff',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {Array.from({ length: new Date().getFullYear() - 2024 }, (_, i) => 2025 + i).map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <ActionButton onClick={handleSignedWaiversSearch} disabled={signedWaiversLoading}>
                  {signedWaiversLoading ? 'Searching...' : 'Search'}
                </ActionButton>
                {signedWaiversSearch && (
                  <ActionButton onClick={handleClearSignedWaiversSearch}>
                    Clear
                  </ActionButton>
                )}
              </SearchRow>
            </SearchSection>

            <ResultsSection>
              <ResultsHeader>
                <ResultsTitle>
                  Signed Waivers ({signedWaiversTotalCount})
                  {signedWaiversSearch && ` - searching "${signedWaiversSearch}"`}
                </ResultsTitle>
              </ResultsHeader>
              {signedWaiversLoading ? (
                <EmptyState>Loading signed waivers...</EmptyState>
              ) : signedWaivers.length > 0 ? (
                <>
                  <ResultsList>
                    {signedWaivers.map((waiver) => {
                      const displayName = waiver.signerType === 'guest'
                        ? waiver.guestInfo?.fullName || waiver.formData?.fullName || 'Unknown Guest'
                        : waiver.formData?.fullName || 'Unknown';
                      const isGuest = waiver.signerType === 'guest';
                      return (
                        <PlayerCard key={waiver.id} $status="valid">
                          <PlayerInfo>
                            <PlayerAvatar $status="valid">
                              {getInitials(displayName)}
                            </PlayerAvatar>
                            <PlayerDetails>
                              <PlayerName>
                                {displayName}
                                {isGuest && <span style={{ color: '#ff9800', marginLeft: '8px', fontSize: '12px' }}>(Guest)</span>}
                              </PlayerName>
                              <PlayerMeta>
                                {waiver.formData?.dateOfBirth && `DOB: ${new Date(waiver.formData.dateOfBirth).toLocaleDateString()} • `}
                                {waiver.formData?.email || waiver.guestInfo?.email || 'No email'}
                                {(waiver.formData?.phone || waiver.guestInfo?.phone) && ` • ${waiver.formData?.phone || waiver.guestInfo?.phone}`}
                              </PlayerMeta>
                              <PlayerMeta style={{ marginTop: '4px', opacity: 0.7 }}>
                                Signed: {new Date(waiver.signedAt).toLocaleDateString()} {new Date(waiver.signedAt).toLocaleTimeString()}
                              </PlayerMeta>
                            </PlayerDetails>
                          </PlayerInfo>
                          <PlayerActions>
                            <WaiverBadge $status="valid">{waiver.year}</WaiverBadge>
                          </PlayerActions>
                        </PlayerCard>
                      );
                    })}
                  </ResultsList>
                  {signedWaiversTotalPages > 1 && (
                    <div style={{
                      padding: '16px 24px',
                      borderTop: '1px solid #333',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <ActionButton
                        onClick={() => setSignedWaiversPage(p => Math.max(1, p - 1))}
                        disabled={signedWaiversPage === 1}
                      >
                        Previous
                      </ActionButton>
                      <span style={{ color: '#fff', fontWeight: 600 }}>
                        Page {signedWaiversPage} of {signedWaiversTotalPages}
                      </span>
                      <ActionButton
                        onClick={() => setSignedWaiversPage(p => Math.min(signedWaiversTotalPages, p + 1))}
                        disabled={signedWaiversPage === signedWaiversTotalPages}
                      >
                        Next
                      </ActionButton>
                    </div>
                  )}
                </>
              ) : (
                <EmptyState>
                  {signedWaiversSearch
                    ? `No waivers found for "${signedWaiversSearch}" in ${signedWaiversYear}`
                    : `No signed waivers for ${signedWaiversYear}`}
                </EmptyState>
              )}
            </ResultsSection>
          </>
        )}

        {activeTab === 'templates' && (
          <ResultsSection>
            <ResultsHeader>
              <ResultsTitle>Waiver Templates ({templates.length})</ResultsTitle>
              <ActionButton onClick={() => navigate('/field/waiver/builder')}>
                + Create Template
              </ActionButton>
            </ResultsHeader>
            {templatesLoading ? (
              <EmptyState>Loading templates...</EmptyState>
            ) : templates.length > 0 ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', padding: '20px' }}>
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      style={{
                        background: '#1a1a2e',
                        border: '1px solid #333',
                        borderRadius: '12px',
                        padding: '20px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                        <div>
                          <div style={{ color: '#fff', fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>{template.name}</div>
                          <div style={{ color: '#888', fontSize: '13px' }}>{template.year} · Version {template.version}</div>
                        </div>
                        {template.isActive ? (
                          <WaiverBadge $status="valid">Active</WaiverBadge>
                        ) : template.isLocked ? (
                          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, background: 'rgba(255, 193, 7, 0.15)', color: '#ffc107' }}>Locked</span>
                        ) : (
                          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 500, background: 'rgba(255, 255, 255, 0.1)', color: '#888' }}>Draft</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#0d0d1a', borderRadius: '8px' }}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: '#00d4ff' }}>{template.signatureCount}</div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Signatures</div>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#0d0d1a', borderRadius: '8px' }}>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: '#00d4ff' }}>{template.sections.length}</div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Sections</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <ActionButton onClick={() => navigate(`/field/waiver/builder/${template.id}`)}>
                          {template.isLocked ? 'View' : 'Edit'}
                        </ActionButton>
                        {!template.isActive && (
                          <ActionButton $variant="success" onClick={() => handleActivateTemplate(template.id)}>
                            Activate
                          </ActionButton>
                        )}
                        <ActionButton onClick={() => setCopyModal({ show: true, templateId: template.id })}>
                          Copy
                        </ActionButton>
                        {!template.isLocked && (
                          <ActionButton $variant="danger" onClick={() => handleDeleteTemplate(template.id)}>
                            Delete
                          </ActionButton>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '16px 24px', borderTop: '1px solid #333', display: 'flex', gap: '12px' }}>
                  <ActionButton $variant="success" onClick={() => navigate(`/waiver/${field?.id}`)}>
                    Preview Current Waiver
                  </ActionButton>
                </div>
              </>
            ) : (
              <EmptyState>
                <p>No waiver templates yet. Create your first template to get started.</p>
                <div style={{ marginTop: '20px' }}>
                  <ActionButton onClick={() => navigate('/field/waiver/builder')}>
                    + Create Template
                  </ActionButton>
                </div>
              </EmptyState>
            )}
          </ResultsSection>
        )}
      </MainContent>

      {/* Copy Template Modal */}
      {copyModal.show && (
        <ModalOverlay onClick={() => setCopyModal({ show: false, templateId: null })}>
          <GameTypeModal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Copy Template</ModalTitle>
            <ModalSubtitle>Select the target year for the new template copy.</ModalSubtitle>
            <select
              value={copyYear}
              onChange={(e) => setCopyYear(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #444',
                borderRadius: '8px',
                background: '#1a1a1a',
                color: '#fff',
                fontSize: '14px',
                marginBottom: '16px',
              }}
            >
              {[...Array(5)].map((_, i) => {
                const y = new Date().getFullYear() + i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <ModalActions>
              <ModalButton onClick={() => setCopyModal({ show: false, templateId: null })}>
                Cancel
              </ModalButton>
              <ModalButton $variant="primary" onClick={handleCopyTemplate}>
                Copy Template
              </ModalButton>
            </ModalActions>
          </GameTypeModal>
        </ModalOverlay>
      )}

      {showGameTypeModal && selectedCheckIn && (
        <ModalOverlay onClick={closeConfirmModal}>
          <GameTypeModal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Confirm Check-In</ModalTitle>
            <ModalSubtitle>Review player information and select game type</ModalSubtitle>

            <PlayerInfoBox>
              <PlayerInfoRow>
                <PlayerInfoLabel>Name</PlayerInfoLabel>
                <PlayerInfoValue>{selectedCheckIn.playerInfo.name}</PlayerInfoValue>
              </PlayerInfoRow>
              {selectedCheckIn.playerInfo.email && (
                <PlayerInfoRow>
                  <PlayerInfoLabel>Email</PlayerInfoLabel>
                  <PlayerInfoValue>{selectedCheckIn.playerInfo.email}</PlayerInfoValue>
                </PlayerInfoRow>
              )}
              {selectedCheckIn.playerInfo.phone && (
                <PlayerInfoRow>
                  <PlayerInfoLabel>Phone</PlayerInfoLabel>
                  <PlayerInfoValue>{selectedCheckIn.playerInfo.phone}</PlayerInfoValue>
                </PlayerInfoRow>
              )}
              <PlayerInfoRow>
                <PlayerInfoLabel>Waiver Status</PlayerInfoLabel>
                <WaiverBadge $status={selectedCheckIn.waiverStatus?.hasValidWaiver ? 'valid' : 'none'}>
                  {selectedCheckIn.waiverStatus?.hasValidWaiver ? 'Waiver OK' : 'No Waiver'}
                </WaiverBadge>
              </PlayerInfoRow>
              {selectedCheckIn.playerInfo.isGuest && (
                <PlayerInfoRow>
                  <PlayerInfoLabel>Type</PlayerInfoLabel>
                  <PlayerInfoValue>Guest</PlayerInfoValue>
                </PlayerInfoRow>
              )}
            </PlayerInfoBox>

            <div style={{ marginBottom: '12px', color: '#888', fontSize: '13px' }}>Select Game Type</div>
            <GameTypeGrid>
              {GAME_TYPES.map((type) => (
                <GameTypeButton
                  key={type}
                  $selected={selectedGameType === type}
                  onClick={() => setSelectedGameType(type)}
                >
                  {type}
                </GameTypeButton>
              ))}
            </GameTypeGrid>

            <ModalActions>
              <ModalButton onClick={closeConfirmModal}>Cancel</ModalButton>
              <ModalButton $variant="primary" onClick={handleConfirmCheckIn}>
                Confirm Check-In
              </ModalButton>
            </ModalActions>
          </GameTypeModal>
        </ModalOverlay>
      )}

      {showRfidModal && rfidCheckIn && (
        <ModalOverlay onClick={closeRfidModal}>
          <GameTypeModal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{rfidCheckIn.rfidTag ? 'Update RFID Tag' : 'Add RFID Tag'}</ModalTitle>
            <ModalSubtitle>
              {rfidCheckIn.playerInfo.fullName || rfidCheckIn.playerInfo.name}
            </ModalSubtitle>

            <RfidInput
              type="text"
              placeholder="Scan or enter RFID tag..."
              value={newRfidTag}
              onChange={(e) => setNewRfidTag(e.target.value)}
              autoFocus
            />

            <ModalActions>
              <ModalButton onClick={closeRfidModal}>Cancel</ModalButton>
              <ModalButton $variant="primary" onClick={handleUpdateRfid} disabled={!newRfidTag.trim()}>
                Update RFID
              </ModalButton>
            </ModalActions>
          </GameTypeModal>
        </ModalOverlay>
      )}

      <ConfirmModal {...modalProps} />
    </PageContainer>
    </FeatureGate>
  );
};

export default FieldManagementPage;
